import { useEffect, useState } from 'react';
import { Tag, Image, Tabs, Spin } from 'antd';
import { TemplateDetails } from 'PhishingManager/pages/CampaignSteps';
import CommonModal from 'PhishingManager/components/Modal';

interface PhishingModalProps {
    loading: boolean;
    visible: boolean;
    onClose: () => void;
    onNext: () => void;
    onLanguageChange: (language: number) => void;
    templateDetails: TemplateDetails | null;
    showConfirmBtn: boolean;
}

const PhishingModal = ({ loading, visible, onClose, onNext, onLanguageChange, templateDetails, showConfirmBtn }: PhishingModalProps) => {
    const [activeTab, setActiveTab] = useState('email');
    const { TabPane } = Tabs;

    const handleTemplateChange = (lang: { id: number, name?: string | undefined }) => {
        onLanguageChange(lang.id);
    }

    const openInNewTab = (htmlContent: string) => {
        if (!htmlContent) return;
        const newWindow = window.open();
        newWindow?.document.write(htmlContent);
        newWindow?.document.close();
    };

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data === 'IFRAME_EMAIL_CLICKED') {
                openInNewTab(templateDetails?.email_template_html ?? "");
            }
            if (event.data === 'IFRAME_LANDING_CLICKED') {
                openInNewTab(templateDetails?.landing_page_html ?? "");
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [templateDetails]);

    return (
        <CommonModal
            visible={visible}
            showConfirmBtn={showConfirmBtn}
            confirmText="Next"
            cancelText="Close"
            iconType={null}
            onClose={onClose}
            onConfirm={onNext}
            modalTitle={templateDetails?.name || "Template Name"}
            parentClass='phishing-preview-modal'>
            <Spin spinning={loading}>
                <div className="description-section">
                    <div className="section-image">
                        <Image
                            src={templateDetails?.image_path || "./placeholderImage.png"}
                            alt="Phishing simulation preview"
                            preview={false}
                        />
                        <Tag color="green" className="badge-beginner">
                            {templateDetails?.difficulty}
                        </Tag>
                    </div>

                    <div className="section-content">
                        <h3 className="description-title">Description</h3>
                        <p className="description-text">
                            {templateDetails?.description || "No description available for this template."}
                        </p>
                        <p className='description-category'><strong>Category: </strong>
                            {templateDetails?.categories?.map(c => c.name).join(', ') || '—'}
                        </p>
                    </div>
                </div>

                <div className="tabs-section">
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <TabPane tab="Email" key="email">
                            <TemplatePreview
                                title="Email Template Preview"
                                html={templateDetails?.email_template_html}
                                postMessageKey="IFRAME_EMAIL_CLICKED"
                            />
                        </TabPane>

                        <TabPane tab="Landing Page" key="landing_page">
                            <TemplatePreview
                                title="Landing Page Preview"
                                html={templateDetails?.landing_page_html}
                                postMessageKey="IFRAME_LANDING_CLICKED"
                            />
                        </TabPane>
                    </Tabs>
                </div>

                <div className="language-section">
                    <label className="language-label">
                        Select Language <span className="required">*</span>
                    </label>
                    <div className="language-tags">
                        {templateDetails?.languages?.map((lang: { id: number, name?: string | undefined }) => (
                            <Tag key={lang.id}
                                onClick={() => handleTemplateChange(lang)}
                                className={`language-tag ${templateDetails?.selected_packaged_template_language_id === lang.id ? 'selected' : ''}`}>
                                {lang.name}
                            </Tag>
                        ))}
                    </div>
                </div>
            </Spin>
        </CommonModal>
    );
};

export default PhishingModal;

const TemplatePreview = ({ title, html, postMessageKey }: { title: string; html?: string, postMessageKey: string }) => {

    const injectIframeLogic = (html: string | undefined): string => {
        if (!html) return "";

        const logicScript = `
            <style>
            a { pointer-events: none !important; cursor: default !important; }
            html, body { cursor: pointer; margin: 0; padding: 0; }
            </style>
            <script>
            document.addEventListener('click', () => {
                window.parent.postMessage('${postMessageKey}', '*');
            });
            </script>
        `;

        if (html.includes("</head>")) return html.replace("</head>", `${logicScript}</head>`);
        if (html.includes("<body>")) return html.replace("<body>", `<head>${logicScript}</head><body>`);
        return `<head>${logicScript}</head>` + html;
    };

    return (
        <div className="tab-content">
            <div>
                <iframe title={title} srcDoc={injectIframeLogic(html) || ""} />
            </div>
        </div>
    );
};